import { isRichAggregateSummary, pickBestAggregateSummary } from '../../../utils/aggregateSummaryPick';

const SUMMARY_KEYS = [
  'avg_metrics',
  'audit_avg_metrics',
  'audited_avg_metrics',
  'sensitive_density',
  'sensitive_by_source',
  'non_whitelisted_sensitive_by_source',
  'attribute_mentions',
  'verdict_distribution',
  'funnel',
  'metric_thresholds',
  'thresholds',
  'vulnerability_surface',
  'developer_recommendations',
  'microeai_bias_score',
  'risk_distribution',
  'dataset_verdict'
] as const;

export const isNonEmptyRecord = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length > 0;

const isPopulated = (value: unknown): boolean =>
  value != null && (typeof value !== 'object' || Array.isArray(value) || Object.keys(value as object).length > 0);

/** Flatten collection run / 360 bias node into chart-ready aggregate summary (incl. hoisted funnel). */
export const resolveBiasAggregateSummary = (node: unknown): Record<string, unknown> | undefined => {
  if (!isNonEmptyRecord(node)) return undefined;

  const base: Record<string, unknown> = { ...((node.aggregate_summary as Record<string, unknown>) ?? {}) };
  for (const key of SUMMARY_KEYS) {
    if (!isPopulated(base[key]) && isPopulated(node[key])) {
      base[key] = node[key];
    }
  }
  if (isRichAggregateSummary(base) || Object.keys(base).length > 0) {
    return base;
  }
  return isRichAggregateSummary(node) ? { ...node } : undefined;
};

/** Merge 360 bias with project results; empty `{}` from 360 does not block funnel from results. */
export const mergeBiasAggregateSummaries = (...candidates: unknown[]): Record<string, unknown> | undefined => {
  const resolved = candidates.map(resolveBiasAggregateSummary).filter(isNonEmptyRecord);
  if (resolved.length === 0) return undefined;

  const merged = { ...pickBestAggregateSummary(resolved)! };
  for (const source of resolved) {
    for (const key of SUMMARY_KEYS) {
      if (!isPopulated(merged[key]) && isPopulated(source[key])) {
        merged[key] = source[key];
      }
    }
  }
  return Object.keys(merged).length > 0 ? merged : undefined;
};
